import { Fragment, useCallback, useEffect, useState } from 'react';
import { useEffectOnce } from 'react-use';

import calendar from '~/assets/icons/calendar.svg';
import percent from '~/assets/icons/percent.svg';
import { rage, useObservable } from '~/rage';
import { BankOperationType, Currency } from '~/shared';

import home from '~/assets/icons/home.svg';
import { CurrencyIcon } from '~/components/collections';
import { Button, ButtonColors, ButtonHeight } from '~/components/ui/Button';
import { Text } from '~/components/ui/Text';
import { useWalletIcon } from '~/hooks';
import {
	setTaxPaymentDays,
	setTaxPaymentSelectedId,
	useLayoutDispatch,
	useLayoutSelector,
} from '../../redux';
import { DaysInput } from '../DaysInput';
import { MoneyButton } from '../MoneyButton';
import { SectionHeading } from '../SectionHeading';
import {
	StyledButtonContent,
	StyledContainer,
	StyledDaysButtonsContainer,
	StyledHomeSelectorContainer,
	StyledMoneyButtonsFirstRow,
	StyledSectionContainer,
	StyledSummaryContainer,
} from './TaxPayment.styled';

const TaxPayment: React.FC = () => {
	const dispatch = useLayoutDispatch();
	const [ownedHouses, setOwnedHouses] = useState<string[]>([]);
	const activeHouse = useLayoutSelector(
		(state) => state.bank.taxPaymentSelectedId,
	);
	const days = useLayoutSelector((state) => state.bank.taxPaymentDays);
	const [loading, setLoading] = useState<boolean>(false);
	const [paidDays, setPaidDays] = useState<number>(0);
	const rubleIcon = useWalletIcon(Currency.Ruble, true);
	const maxPayDays = rage.houses.getMaxPaidDays();
	const dayPrice =
		activeHouse == '' ? 0 : rage.houses.getPriceForDay(activeHouse);
	const fee = useObservable(rage.bank.getTransferFee(BankOperationType.TAX));

	const handlePay = useCallback(() => {
		setLoading(true);
		rage.houses
			.pay(activeHouse, days)
			.then(() => {
				setLoading(false);
				setPaidDays(Math.min(paidDays + days, maxPayDays));
			})
			.catch((err) => rage.notifications.showException(err));
	}, [activeHouse, days]);

	useEffectOnce(() => {
		rage.houses
			.getOwnedHousesIds()
			.then((value) => {
				setOwnedHouses(value);
				if (value.length > 0) {
					dispatch(setTaxPaymentSelectedId(value[0]));
				}
			})
			.catch((err) => rage.notifications.showException(err));
	});

	useEffect(() => {
		if (activeHouse) {
			rage.houses
				.getById(activeHouse)
				.then((house) => {
					setPaidDays(house?.daysPaid ?? 0);
				})
				.catch((err) => rage.notifications.showException(err));
		}
	}, [activeHouse]);

	useEffect(() => {
		if (days > maxPayDays - paidDays) {
			dispatch(setTaxPaymentDays(maxPayDays - paidDays));
		}
	}, [days, maxPayDays, paidDays]);

	return (
		<StyledContainer>
			<StyledSectionContainer>
				<SectionHeading icon={home} text={'Выберите имущество'} />
				<StyledHomeSelectorContainer>
					{ownedHouses.map((item, index) => (
						<MoneyButton
							key={index}
							text={`Дом #${item}`}
							active={item === activeHouse}
							onClick={() => {
								dispatch(setTaxPaymentSelectedId(item));
							}}
							icon={home}
						/>
					))}
				</StyledHomeSelectorContainer>
			</StyledSectionContainer>
			<StyledSectionContainer>
				<SectionHeading icon={calendar} text={'Укажите кол-во дней'} />
				<DaysInput
					value={days}
					changeHandler={(value: number) => dispatch(setTaxPaymentDays(value))}
					payDays={paidDays}
					maxDays={maxPayDays}
				/>
				<StyledDaysButtonsContainer>
					<StyledMoneyButtonsFirstRow>
						<MoneyButton
							text={'1 день'}
							onClick={() => dispatch(setTaxPaymentDays(days + 1))}
							icon={calendar}
						/>
						<MoneyButton
							text={'5 дней'}
							onClick={() => dispatch(setTaxPaymentDays(days + 5))}
							icon={calendar}
						/>
						<MoneyButton
							text={'7 дней'}
							onClick={() => dispatch(setTaxPaymentDays(days + 7))}
							icon={calendar}
						/>
					</StyledMoneyButtonsFirstRow>
					<MoneyButton
						text={'14 дней'}
						onClick={() => dispatch(setTaxPaymentDays(days + 14))}
						icon={calendar}
					/>
				</StyledDaysButtonsContainer>
			</StyledSectionContainer>
			<StyledSummaryContainer>
				<Text>Вы заплатите</Text>
				<Text>
					{Math.floor(days * dayPrice + days * dayPrice * fee).toLocaleString(
						'ru-RU',
					)}
				</Text>
				<CurrencyIcon icon={rubleIcon} size="sm" />
				{fee ? (
					<Fragment>
						<Text>Комиссия банка</Text>
						<Text>{(fee * 100).toLocaleString('ru-RU')}</Text>
						<img src={percent} width={14} height={14} />
					</Fragment>
				) : null}
			</StyledSummaryContainer>
			<Button
				color={ButtonColors.Select2}
				height={ButtonHeight.Height60}
				disabled={loading || days === 0}
				onClick={handlePay}
				style={{ position: 'absolute', bottom: 0, width: '100%', fontSize: 18 }}
			>
				<StyledButtonContent>
					{days !== 0 && <img src={home} width={20} height={20} />}
					<Text>{days === 0 ? 'Выберите кол-во дней' : 'Оплатить налоги'}</Text>
				</StyledButtonContent>
			</Button>
		</StyledContainer>
	);
};

export { TaxPayment };
